"use client";
import React, { useState } from "react";
import StartsFromCard from "@components/molecules/StartsFromCard";
import MileageModal from "@components/molecules/MileageModal";
import { formatDeliveryDate } from "@utils/formatDate";
import { formatToLakhs } from "@utils/numberFormatter";
import { trackEventWithCommonProps, EventType } from "@utils/eventTracker";
import { PAGE_TYPES } from "@constants/pageTypes";
import { VariantContentBlockProps } from "./VariantContentBlock.types";
import styles from "./variantContentBlock.module.scss";

interface VariantContentBlockClientWrapperProps {
  startsFromCards: VariantContentBlockProps["startsFromCards"];
  isSellable: boolean;
  isLoggedIn: boolean;
  cityName?: string;
  makeName?: string;
  modelName?: string;
  pageType?: string;
  carAvailability?: string;
}

export default function VariantContentBlockClientWrapper({
  startsFromCards,
  isSellable,
  isLoggedIn,
  cityName,
  makeName,
  modelName,
  pageType,
  carAvailability,
}: VariantContentBlockClientWrapperProps) {
  const [modalData, setModalData] = useState<{
    type: string;
    count: number;
  } | null>(null);

  const getMatchingVariants = (type: string) =>
    startsFromCards?.filter(
      (variant) => variant.key.toLowerCase() === type.toLowerCase()
    )[0].variants || [];

  function transformCarDataForMileageModal(rawData: any[]) {
    return rawData.map((item) => ({
      name: item?.variant?.name,
      fuelType: item?.fuelType,
      transmission: item?.transmission,
      mileage: item?.mileage?.value,
      formattedPrice: formatToLakhs(item?.minColorPrice),
      expressDelivery: item?.expressDeliveryFlag,
      expressDeliveryDate: formatDeliveryDate(item?.minDeliveryDays),
      standardDelivery: formatDeliveryDate(item?.minStandardDeliveryDays),
      slug: item?.slug,
      status: item?.status,
    }));
  }

  // Event tracking for summary section click
  const handleSummaryClick = async (type: string, count: number) => {
    await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
      pageType: pageType || PAGE_TYPES.VARIANT_PAGE,
      additionalProps: {
        action_type: "tap_btn",
        action_name: "tap_btn_choose_a_variant",
        section: "Variant page summary",
        selected_value: type,
        car_availability: carAvailability,
        city_name: cityName,
        vehicle_make: makeName,
        vehicle_model: modelName,
      },
    });

    setModalData({
      type: type,
      count: count,
    });
  };

  return (
    <>
      <div className={styles.startsFromContainer}>
        {startsFromCards.map((value, index) => {
          return (
            <StartsFromCard
              key={`${value.key} ${index}`}
              type={value.key}
              startsFrom={`${formatToLakhs(value.minValue)} L`}
              variants={value.variants.length.toString()}
              onClick={() =>
                handleSummaryClick(value.key, value.variants.length)
              }
            />
          );
        })}
      </div>

      <MileageModal
        isOpen={!!modalData}
        onClose={() => setModalData(null)}
        title={
          modalData
            ? `Available in ${modalData.count} variant${
                modalData.count > 1 ? "s" : ""
              }`
            : "Available in number of variants"
        }
        variants={
          modalData
            ? transformCarDataForMileageModal(
                getMatchingVariants(modalData.type)
              )
            : []
        }
        preventScoll={true}
        isSellable={isSellable}
        isLoggedIn={isLoggedIn}
        cityName={cityName}
        brandName={makeName}
        modelName={modelName}
        segmentPageType={pageType}
        segmentActionName="tap_btn_variant"
        segmentSection="Variant page summary"
        carAvailability={carAvailability}
      />
    </>
  );
}
